import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { DatePicker } from '../ui/DatePicker';
import { useNotifications } from '../../contexts/NotificationContext';
import { OperativeActivity, getStepTitlesForActivity, operativeActivitiesStore } from '../../services/operativeActivities';
import { CheckCircle2, Circle, FileText, Settings2 } from 'lucide-react';
interface EditActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  activity: OperativeActivity | null;
  onSave: () => void;
}
export function EditActivityModal({
  isOpen,
  onClose,
  activity,
  onSave
}: EditActivityModalProps) {
  const {
    addNotification
  } = useNotifications();
  const [activeTab, setActiveTab] = useState<string>('generali');
  // General Data State
  const [title, setTitle] = useState('');
  const [operator, setOperator] = useState('');
  const [deadline, setDeadline] = useState('');
  const [product, setProduct] = useState('');
  // Steps Data State
  const [stepsCompleted, setStepsCompleted] = useState<boolean[]>([]);
  const [customStepNames, setCustomStepNames] = useState<string[]>([]);
  useEffect(() => {
    if (activity && isOpen) {
      setTitle(activity.title);
      setOperator(activity.operator);
      setDeadline(activity.deadline);
      setProduct(activity.product);
      setStepsCompleted([...activity.stepsCompleted]);
      if (activity.activityType === 'personalizzata' && activity.customStepNames) {
        setCustomStepNames([...activity.customStepNames]);
      } else {
        setCustomStepNames([]);
      }
      setActiveTab('generali');
    }
  }, [activity, isOpen]);
  if (!activity) return null;
  const stepTitles = getStepTitlesForActivity(activity);
  const isCustom = activity.activityType === 'personalizzata';
  const handleSaveGeneral = () => {
    operativeActivitiesStore.updateActivity(activity.id, {
      title,
      operator,
      deadline,
      product
    });
    addNotification('success', 'Modifiche salvate', 'Dati generali aggiornati con successo.');
    onSave();
  };
  const handleSaveStep = (index: number) => {
    // For custom activities, we might have updated the step name
    const updates: Partial<OperativeActivity> = {
      stepsCompleted: [...stepsCompleted]
    };
    if (isCustom) {
      updates.customStepNames = [...customStepNames];
    }
    operativeActivitiesStore.updateActivity(activity.id, updates);
    addNotification('success', 'Modifiche salvate', `Step "${isCustom ? customStepNames[index] : stepTitles[index]}" aggiornato con successo.`);
    onSave();
  };
  const handleToggleStep = (index: number) => {
    const newCompleted = [...stepsCompleted];
    newCompleted[index] = !newCompleted[index];
    // If unchecking a step, uncheck all subsequent steps
    if (!newCompleted[index]) {
      for (let i = index + 1; i < newCompleted.length; i++) {
        newCompleted[i] = false;
      }
    }
    // If checking a step, check all previous steps
    else {
      for (let i = 0; i < index; i++) {
        newCompleted[i] = true;
      }
    }
    setStepsCompleted(newCompleted);
  };
  const handleCustomStepNameChange = (index: number, newName: string) => {
    const newNames = [...customStepNames];
    newNames[index] = newName;
    setCustomStepNames(newNames);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={`Modifica Attività: ${activity.code || activity.title}`} size="2xl" data-id="element-502" data-mp="2dyf3k39iq0s5">
      <div className="flex flex-col md:flex-row gap-6 min-h-[500px]" data-id="element-503" data-mp="28xhnpxbzditt">
        {/* Sidebar */}
        <div className="w-full md:w-64 flex-shrink-0 border-r border-slate-100 pr-6" data-id="element-504" data-mp="23zg0r1bfozd2">
          <div className="space-y-1" data-id="element-505" data-mp="xkowcnn3qwea">
            <button onClick={() => setActiveTab('generali')} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === 'generali' ? 'bg-seform-orange/10 text-seform-orange' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-506" data-mp="xhj6h66nfqhi">
              <Settings2 className="h-4 w-4" data-id="element-507" data-mp="27hlnqhaoanll" />
              Dati Generali
            </button>

            <div className="pt-4 pb-2" data-id="element-508" data-mp="2cfdmnv9qj9za">
              <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-509" data-mp="2cg5aul1065fj">
                Step Operativi
              </p>
            </div>

            {stepTitles.map((stepName, index) => {
            const isCompleted = stepsCompleted[index];
            const displayTitle = isCustom && customStepNames[index] ? customStepNames[index] : stepName;
            return <button key={index} onClick={() => setActiveTab(`step-${index}`)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${activeTab === `step-${index}` ? 'bg-seform-orange/10 text-seform-orange' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-510" data-mp="1erloaxyxy2q4" data-mpi={index}>
                  {isCompleted ? <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" data-id="element-511" data-mp="1l7ly8d9xte99" /> : <Circle className="h-4 w-4 text-slate-300 flex-shrink-0" data-id="element-512" data-mp="1g77o8porzu8l" />}
                  <span className="truncate" data-id="element-513" data-mp="shn3q2cz4ox7">{displayTitle}</span>
                </button>;
          })}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 pl-2" data-id="element-514" data-mp="12aayysob2ktt">
          {activeTab === 'generali' && <div className="space-y-6 animate-in fade-in duration-200" data-id="element-515" data-mp="xhrm5xxfztq2">
              <div className="flex items-center justify-between mb-6" data-id="element-516" data-mp="9synvutcl9sa">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2" data-id="element-517" data-mp="9r5ck27si1dy">
                  <FileText className="h-5 w-5 text-slate-400" data-id="element-518" data-mp="4s2krjgtlrnx" />
                  Dati Generali
                </h3>
                <Badge variant={activity.activityType === 'standard' ? 'info' : 'warning'} data-id="element-519" data-mp="29vho9758plhg">
                  {activity.activityType === 'standard' ? 'Standard' : 'Personalizzata'}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-520" data-mp="1lykdpincblvu">
                <Input label="Titolo attività" value={title} onChange={e => setTitle(e.target.value)} className="col-span-full" data-id="element-521" data-mp="1qwdg00livvqu" />

                <Select label="Prodotto" value={product} onChange={e => setProduct(e.target.value)} options={[{
              value: 'Consulenza Sicurezza',
              label: 'Consulenza Sicurezza'
            }, {
              value: 'HACCP Base',
              label: 'HACCP Base'
            }, {
              value: 'Privacy GDPR',
              label: 'Privacy GDPR'
            }, {
              value: 'DVR',
              label: 'DVR'
            }, {
              value: 'Formazione Lavoratori',
              label: 'Formazione Lavoratori'
            }, {
              value: 'Consulenza Ambiente',
              label: 'Consulenza Ambiente'
            }, {
              value: 'Antiriciclaggio',
              label: 'Antiriciclaggio'
            }]} data-id="element-522" data-mp="1zxjz8hfk3kns" />

                <Select label="Operatore assegnato" value={operator} onChange={e => setOperator(e.target.value)} options={[{
              value: 'Alessio',
              label: 'Alessio'
            }, {
              value: 'Lorenzo',
              label: 'Lorenzo'
            }, {
              value: 'Gian Franco',
              label: 'Gian Franco'
            }, {
              value: 'Tiziano',
              label: 'Tiziano'
            }]} data-id="element-523" data-mp="1xcly4uhp5oeu" />

                <DatePicker label="Scadenza" value={deadline} onChange={e => setDeadline(e.target.value)} data-id="element-524" data-mp="dkyqhct7qeje" />

                <Input label="Cliente" value={activity.clientName} disabled className="bg-slate-50" data-id="element-525" data-mp="27xeu05umjekk" />

                <Input label="Contratto" value={activity.contractNumber} disabled className="bg-slate-50" data-id="element-526" data-mp="194nai1sw2qt" />
              </div>

              <div className="pt-6 mt-6 border-t border-slate-100 flex justify-end" data-id="element-527" data-mp="azqxtxunu32n">
                <Button onClick={handleSaveGeneral} data-id="element-528" data-mp="664vayi7nfkk">
                  Salva modifiche generali
                </Button>
              </div>
            </div>}

          {activeTab.startsWith('step-') && <div className="space-y-6 animate-in fade-in duration-200" data-id="element-529" data-mp="f242eixo7uxu">
              {(() => {
            const stepIndex = parseInt(activeTab.split('-')[1]);
            const isCompleted = stepsCompleted[stepIndex];
            const displayTitle = isCustom && customStepNames[stepIndex] ? customStepNames[stepIndex] : stepTitles[stepIndex];
            return <>
                    <div className="flex items-center justify-between mb-6" data-id="element-530" data-mp="3sjabrxovhgje">
                      <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2" data-id="element-531" data-mp="3nimgv0xiwfjw">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-xs font-bold text-slate-500" data-id="element-532" data-mp="3ij9ltswi0268">
                          {stepIndex + 1}
                        </span>
                        Dettaglio Step
                      </h3>
                      <Badge variant={isCompleted ? 'success' : 'warning'} data-id="element-533" data-mp="a4j7wj8uqhkx">
                        {isCompleted ? 'Completato' : 'Da completare'}
                      </Badge>
                    </div>

                    <div className="bg-slate-50 p-6 rounded-xl border border-slate-100 space-y-6" data-id="element-534" data-mp="eptlahbrocz">
                      {isCustom ? <Input label="Nome Step" value={customStepNames[stepIndex] || ''} onChange={e => handleCustomStepNameChange(stepIndex, e.target.value)} placeholder="Inserisci il nome dello step..." data-id="element-535" data-mp="xulvam1tmzd7" /> : <div data-id="element-536" data-mp="1fd3tktqc5mhr">
                          <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-537" data-mp="1kalvw19ul3gs">
                            Nome Step
                          </label>
                          <p className="text-slate-900 font-medium p-3 bg-white rounded-lg border border-slate-200" data-id="element-538" data-mp="1oao0ojavqd0a">
                            {displayTitle}
                          </p>
                        </div>}

                      <div className="pt-4 border-t border-slate-200" data-id="element-539" data-mp="2313j5qbwv6ur">
                        <label className="flex items-center gap-3 cursor-pointer group" data-id="element-540" data-mp="22z0ming96fh2">
                          <div className="relative flex items-center justify-center" data-id="element-541" data-mp="280g8qiu62s11">
                            <input type="checkbox" className="peer sr-only" checked={isCompleted} onChange={() => handleToggleStep(stepIndex)} data-id="element-542" data-mp="1ickj78x2pisy" />
                            <div className="w-6 h-6 rounded border-2 border-slate-300 peer-checked:border-seform-orange peer-checked:bg-seform-orange transition-colors group-hover:border-seform-orange" data-id="element-543" data-mp="1nadpe1ydmct8"></div>
                            <CheckCircle2 className="absolute w-4 h-4 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" data-id="element-544" data-mp="1w2xpl6ubgpg6" />
                          </div>
                          <div data-id="element-545" data-mp="261dklkwgsiuy">
                            <p className="text-sm font-medium text-slate-900" data-id="element-546" data-mp="2626l444tyc4n">
                              Segna come completato
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5" data-id="element-547" data-mp="bodxz7to2oe6">
                              {isCompleted ? 'Questo step è stato completato. Deselezionando, verranno deselezionati anche gli step successivi.' : 'Selezionando questo step, verranno automaticamente completati anche gli step precedenti.'}
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div className="pt-6 mt-6 border-t border-slate-100 flex justify-end" data-id="element-548" data-mp="24jojqe3xvdsz">
                      <Button onClick={() => handleSaveStep(stepIndex)} data-id="element-549" data-mp="28muqdmuyr6zz">
                        Salva modifiche step
                      </Button>
                    </div>
                  </>;
          })()}
            </div>}
        </div>
      </div>
    </Modal>;
}